import { FormEvent, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Mail, Loader2, ShieldCheck } from 'lucide-react'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import AuthLayout from '@/components/AuthLayout'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TwoFactorConfirmEmailProps {
  verifyEmailSetupUrl: string
  locale: string
  logoUrl: string
  translations: {
    confirmEmailTitle: string
    confirmEmailDesc: string
    enableButton: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function TwoFactorConfirmEmail({
  verifyEmailSetupUrl,
  locale,
  logoUrl,
  translations: t,
}: TwoFactorConfirmEmailProps) {
  const [otp, setOtp] = useState('')
  const [processing, setProcessing] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post(
      verifyEmailSetupUrl,
      { otp_attempt: otp },
      { onFinish: () => setProcessing(false) },
    )
  }

  return (
    <>
      <Head title={t.confirmEmailTitle} />

      <AuthLayout locale={locale} logoUrl={logoUrl}>
        <div className="rounded-2xl bg-white shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2DD4BF]/10 to-[#8B5CF6]/10">
              <Mail className="h-7 w-7 text-[#8B5CF6]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#252854] mb-1">{t.confirmEmailTitle}</h1>
            <p className="text-sm text-slate-400">{t.confirmEmailDesc}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={setOtp}
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button
              type="submit"
              disabled={processing || otp.length < 6}
              className="w-full h-11 rounded-xl text-sm font-medium bg-gradient-to-r from-[#2DD4BF] to-[#8B5CF6] hover:from-[#14B8A6] hover:to-[#7C3AED] text-white shadow-lg shadow-[#8B5CF6]/20 transition-all duration-200"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  {t.enableButton}
                </span>
              )}
            </Button>
          </form>
        </div>
      </AuthLayout>
    </>
  )
}
